/* Chi sta usando il programma, per tutta l'applicazione.
 *
 * Lo stato ha tre valori e non due, ed e' la distinzione che conta:
 * `undefined` vuol dire "non lo so ancora, sto chiedendo", `null` vuol dire
 * "non e' entrato nessuno". Confonderli fa sbattere in faccia la pagina di
 * accesso a chi e' gia' dentro, per la frazione di secondo che serve alla
 * prima risposta del server.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { ErroreApi, SESSIONE_SCADUTA, autenticazione, type Utente } from "./api";

interface Sessione {
  utente: Utente | null | undefined;
  /** Vero se la persona era dentro e il server ha risposto 401 a una richiesta
   * normale: la pagina di accesso lo dice, invece di lasciare una schermata che
   * non funziona piu'. Si spegne con un nuovo accesso o con "Esci". */
  sessioneScaduta: boolean;
  /** Restituisce il gettone da riportare con il codice a tempo se la verifica
   * in due passaggi e' attiva, oppure `null` se l'accesso e' gia' aperto. */
  entra: (email: string, password: string) => Promise<string | null>;
  completaAccesso: (gettone: string, codice: string) => Promise<void>;
  esci: () => Promise<void>;
  rileggi: () => Promise<void>;
}

const Contesto = createContext<Sessione | null>(null);

export function ProvveditoreSessione({ children }: { children: ReactNode }) {
  const [utente, setUtente] = useState<Utente | null | undefined>(undefined);
  const [sessioneScaduta, setSessioneScaduta] = useState(false);

  // Solo se la persona era davvero dentro: un 401 a vuoto (per esempio la lettura
  // iniziale di `/me`) non deve far comparire "sessione scaduta" a chi non ha
  // ancora fatto l'accesso. Per questo l'ascolto vive solo finche' e' dentro.
  const dentro = utente !== null && utente !== undefined;
  useEffect(() => {
    if (!dentro) return;
    function scaduta() {
      setSessioneScaduta(true);
      setUtente(null);
    }
    window.addEventListener(SESSIONE_SCADUTA, scaduta);
    return () => window.removeEventListener(SESSIONE_SCADUTA, scaduta);
  }, [dentro]);

  const rileggi = useCallback(async () => {
    try {
      setUtente(await autenticazione.chiSono());
    } catch (errore) {
      // Un 401 qui e' la risposta normale per chi non e' entrato, non un
      // guasto: si traduce in "nessuno", non in una schermata d'errore.
      if (errore instanceof ErroreApi && errore.stato === 401) {
        setUtente(null);
        return;
      }
      throw errore;
    }
  }, []);

  useEffect(() => {
    void rileggi().catch(() => setUtente(null));
  }, [rileggi]);

  const entra = useCallback(async (email: string, password: string) => {
    const risposta = await autenticazione.entra(email, password);
    // Con la verifica attiva la password da sola non apre niente: il server
    // restituisce un gettone, e la sessione nasce solo con il codice.
    if ("richiede_secondo_fattore" in risposta) return risposta.token_accesso;
    setSessioneScaduta(false);
    setUtente(risposta);
    return null;
  }, []);

  const completaAccesso = useCallback(async (gettone: string, codice: string) => {
    const aperto = await autenticazione.verificaSecondoFattore(gettone, codice);
    setSessioneScaduta(false);
    setUtente(aperto);
  }, []);

  const esci = useCallback(async () => {
    try {
      await autenticazione.esci();
    } finally {
      // Anche se la chiamata fallisce, qui dentro l'utente e' fuori: il
      // contrario lascerebbe a schermo un'interfaccia che non funziona piu'.
      setSessioneScaduta(false);
      setUtente(null);
    }
  }, []);

  return (
    <Contesto.Provider value={{ utente, sessioneScaduta, entra, completaAccesso, esci, rileggi }}>
      {children}
    </Contesto.Provider>
  );
}

export function useSessione(): Sessione {
  const sessione = useContext(Contesto);
  if (sessione === null) {
    throw new Error("useSessione fuori da ProvveditoreSessione");
  }
  return sessione;
}
